import { useLayoutEffect, useRef, useState } from "react";
import markUrl from "../assets/vgh-mark-white.png";

/**
 * The VGH loading mark -- the animated monogram shown while a page or table body
 * is fetching, in place of an empty or skeleton table.
 *
 * WHY A MASK, NOT AN <img>
 * There is no vector VGH anywhere (the invoice logo is a raster too), so we
 * cannot do what PM Tool and Kerem Fund do -- trace the mark to inline SVG and
 * animate the paths. Instead the white mark PNG is used as an alpha MASK: a
 * brand-ink fill shows through the monogram's shape, and a lighter band sweeping
 * across that fill reads as a sheen. The upside over an <img> is that the fill
 * is CSS, so one asset recolours with the surface and animates for free; all of
 * that lives in `.logo-loader` in styles.css.
 *
 * WHERE IT SITS -- `fill`
 * A full-page / whole-body load (`fill`, the default) centres the mark in what
 * is left of the viewport below it, rather than parking it just under the page
 * header. It measures its own top once and fills from there to the bottom of
 * the window -- the same trick PM Tool's PageLoader uses -- so it does not need
 * to know how tall the header above it happens to be.
 *
 * A sub-section band passes `fill={false}`. Those sit ABOVE other content on a
 * page (a dashboard widget, a second panel under a populated section); filling
 * the viewport there would push their siblings around. They stay compact, sized
 * by `rows` -- roughly the space the old skeleton bars took (~34px each), so
 * swapping bars for the mark does not shift anything.
 */
const ROW_PX = 34;
const COMPACT_MIN = 96;
const FILL_MIN = 240;
/** Slack below the fill height, so a horizontal scrollbar / fractional display
 *  scaling can never make the box a hair taller than the window. */
const FILL_SLACK = 32;

export default function LogoLoader({
  rows = 5,
  fill = true,
  label = "Loading",
}: {
  rows?: number;
  fill?: boolean;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const compact = Math.max(COMPACT_MIN, rows * ROW_PX);
  const [minHeight, setMinHeight] = useState<number>(compact);

  useLayoutEffect(() => {
    if (!fill) {
      setMinHeight(compact);
      return;
    }
    const el = ref.current;
    if (!el) return;
    /* Before paint, so the mark never renders once at the top and then jumps.
       Safe against feeding back into itself: minHeight changes this element's
       height, never its offset from the top of the document. */
    const measure = () =>
      setMinHeight(
        Math.max(
          FILL_MIN,
          Math.round(window.innerHeight - el.getBoundingClientRect().top - FILL_SLACK),
        ),
      );
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fill, compact]);

  return (
    <div
      ref={ref}
      className="logo-loader"
      role="status"
      aria-busy="true"
      aria-live="polite"
      style={{ minHeight }}
    >
      <span
        className="logo-loader__mark"
        style={{ WebkitMaskImage: `url(${markUrl})`, maskImage: `url(${markUrl})` }}
      />
      <span className="state-sr" style={{ position: "absolute", left: -9999 }}>
        {label}.
      </span>
    </div>
  );
}
